import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import Select from "../../components/form/Select";
import AdminFormPage from "../../components/admin/AdminFormPage";
import { adminFormGridClass } from "../../components/admin/adminStyles";
import { SelectOption } from "../../types/select";

interface UserRoleMapping {
  id: string;
  adminUserId: string;
  roleId: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface AdminUser {
  id: string;
  fullName: string;
}

interface Role {
  id: string;
  roleName: string;
}

export default function UserRoleMappingForm() {
  const { mappingId } = useParams<{ mappingId?: string }>();
  const navigate = useNavigate();

  const [mapping, setMapping] = useState<UserRoleMapping>({
    id: mappingId || "",
    adminUserId: "",
    roleId: "",
    createdBy: "",
    createdAt: new Date().toISOString(),
    updatedBy: null,
    updatedAt: null,
  });

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        axiosInstance.get("/AdminUser/getall", { params: { pageSize: 100 } }),
        axiosInstance.get("/Role/getall", { params: { pageSize: 100 } }),
      ]);

      const fetchedUsers = usersRes.data?.users || [];
      const fetchedRoles = rolesRes.data?.roles || [];
      setUsers(fetchedUsers);
      setRoles(fetchedRoles);

      if (mappingId) {
        const mappingRes = await axiosInstance.get(`/UserRoleMapping/get/${mappingId}`);
        const mappingData = { ...mappingRes.data, id: mappingId };

        if (
          mappingData.adminUserId &&
          !fetchedUsers.find((u: AdminUser) => u.id === mappingData.adminUserId)
        ) {
          const userRes = await axiosInstance.get(`/AdminUser/get/${mappingData.adminUserId}`);
          setUsers((prev) => [...prev, userRes.data]);
        }

        if (
          mappingData.roleId &&
          !fetchedRoles.find((r: Role) => r.id === mappingData.roleId)
        ) {
          const roleRes = await axiosInstance.get(`/Role/get/${mappingData.roleId}`);
          setRoles((prev) => [...prev, roleRes.data]);
        }

        setMapping(mappingData);
      }
    } catch (err) {
      setError("Failed to fetch data");
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [mappingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!mapping.adminUserId || !mapping.roleId) {
      setError("User and Role are required.");
      return;
    }

    setSaving(true);
    try {
      if (mappingId) {
        await axiosInstance.put(`/UserRoleMapping/update/${mappingId}`, {
          ...mapping,
          id: mappingId,
        });
      } else {
        await axiosInstance.post(`/UserRoleMapping/add`, mapping);
      }
      navigate("/user-role-mapping");
    } catch (err) {
      setError("Error saving mapping. Please try again.");
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminFormPage
      pageTitle={mappingId ? "Edit User Role Mapping" : "Add User Role Mapping"}
      description="Assign admin users to roles"
      error={error}
      loading={loading}
      onSubmit={handleSubmit}
      listRoute="/user-role-mapping"
      saving={saving}
      saveLabel={mappingId ? "Update Mapping" : "Add Mapping"}
    >
      <div className={adminFormGridClass}>
        <Select
          label="User"
          name="adminUserId"
          value={mapping.adminUserId || ""}
          onChange={(value: string) =>
            setMapping((prev) => ({ ...prev, adminUserId: value }))
          }
          placeholder="Select User"
          options={users.map(
            (user): SelectOption => ({
              value: user.id,
              label: user.fullName,
            })
          )}
        />
        <Select
          label="Role"
          name="roleId"
          options={roles.map(
            (role): SelectOption => ({
              value: role.id,
              label: role.roleName,
            })
          )}
          value={mapping.roleId || ""}
          onChange={(value: string) => setMapping((prev) => ({ ...prev, roleId: value }))}
          placeholder="Select Role"
        />
      </div>
    </AdminFormPage>
  );
}
